import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useTours, useInvalidate } from "@/lib/siteData";
import { uploadSiteImage } from "@/lib/supabase";
import { Card, Field, Area, Toggle, SaveBtn, DelBtn, Ok, Err } from "./adminUI";
import { TourDetailsFields, tourDetailsPayload } from "./TourDetailsFields";
import AddonsAdmin from "./AddonsAdmin";
import { PRICING, tourPath } from "@/lib/booking";

function TourRow({ tour }) {
    const invalidate = useInvalidate();
    const [form, setForm] = useState({ ...tour });
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState("");

    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        setErr("");
        const { error } = await supabase
            .from("tours")
            .update({
                tag: form.tag,
                title: form.title,
                description: form.description,
                duration: form.duration,
                price: form.price === "" ? null : Number(form.price),
                promo_price:
                    form.promo_price === "" ? null : Number(form.promo_price),
                image_url: form.image_url,
                image_alt: form.image_alt,
                wa_message: form.wa_message,
                sort_order: Number(form.sort_order) || 0,
                active: form.active,
                booking_enabled: Boolean(form.booking_enabled),
                booking_mode: form.booking_mode || "per_booking",
                embark_fee: Number(form.embark_fee) || 0,
                ...tourDetailsPayload(form),
            })
            .eq("id", tour.id);
        setSaving(false);
        if (error) setErr("Erro ao salvar: " + error.message);
        else {
            setMsg("Salvo!");
            invalidate("tours");
            setTimeout(() => setMsg(null), 2500);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Excluir o passeio "${form.title}"?`)) return;
        const { error } = await supabase.from("tours").delete().eq("id", tour.id);
        if (error) setErr("Erro ao excluir: " + error.message);
        else invalidate("tours");
    };

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setErr("");
        try {
            const url = await uploadSiteImage(file, "tours");
            set("image_url")(url);
        } catch (ex) {
            setErr("Erro no upload: " + ex.message);
        }
    };

    return (
        <Card className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <Toggle
                    checked={form.active}
                    onChange={set("active")}
                    label={form.active ? "Ativo" : "Inativo"}
                    testid={`tour-active-${tour.id}`}
                />
                <div className="flex items-center gap-3">
                    <Ok msg={msg} />
                    <DelBtn onClick={remove} testid={`tour-delete-${tour.id}`} />
                </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Título" value={form.title} onChange={set("title")} testid={`tour-title-${tour.id}`} />
                <Field label="Etiqueta" value={form.tag} onChange={set("tag")} testid={`tour-tag-${tour.id}`} />
            </div>
            <Area label="Descrição" value={form.description} onChange={set("description")} testid={`tour-desc-${tour.id}`} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Field label="Duração" value={form.duration} onChange={set("duration")} placeholder="Ex.: 30 min" testid={`tour-duration-${tour.id}`} />
                <Field label="Preço (R$)" type="number" value={form.price ?? ""} onChange={set("price")} testid={`tour-price-${tour.id}`} />
                <Field label="Preço promocional (R$)" type="number" value={form.promo_price ?? ""} onChange={set("promo_price")} testid={`tour-promo-${tour.id}`} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="URL da imagem" value={form.image_url} onChange={set("image_url")} testid={`tour-imgurl-${tour.id}`} />
                <Field label="Texto alternativo da imagem" value={form.image_alt} onChange={set("image_alt")} testid={`tour-imgalt-${tour.id}`} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Toggle checked={Boolean(form.booking_enabled)} onChange={set("booking_enabled")} label={form.booking_enabled ? "Reservas online habilitadas" : "Reservas só pelo WhatsApp"} />
                <label className="block text-sm text-dim">Modelo de cobrança
                    <select className="mt-2 w-full rounded-xl border border-ink/15 bg-ocean px-4 py-3 text-sm text-ink" value={form.booking_mode || "per_booking"} onChange={e=>set("booking_mode")(e.target.value)}>
                    {Object.entries(PRICING).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </label>
                <Field label="Taxa por participante (R$)" type="number" value={form.embark_fee ?? 0} onChange={set("embark_fee")} />
            </div>
            <Area label="Mensagem do WhatsApp deste passeio" value={form.wa_message} onChange={set("wa_message")} testid={`tour-wamsg-${tour.id}`} />
            <TourDetailsFields form={form} set={set} id={tour.id} />
            {tour.slug && <p className="text-xs text-dim">Link compartilhável: <a className="text-neon-cyan" href={tourPath(tour)} target="_blank" rel="noreferrer">{window.location.origin}{tourPath(tour)}</a></p>}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Field label="Ordem de exibição" type="number" value={form.sort_order} onChange={set("sort_order")} testid={`tour-order-${tour.id}`} />
                <label className="block">
                    <span className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">
                        Trocar imagem
                    </span>
                    <input
                        type="file"
                        accept="image/*"
                        data-testid={`tour-upload-${tour.id}`}
                        onChange={upload}
                        className="mt-2 block w-full text-sm text-dim file:mr-3 file:rounded-full file:border-0 file:bg-neon-cyan/10 file:px-4 file:py-2 file:font-mono2 file:text-[10px] file:uppercase file:tracking-[0.2em] file:text-neon-cyan"
                    />
                </label>
                <div className="flex items-end justify-end gap-4">
                    <Err msg={err} />
                    <SaveBtn onClick={save} saving={saving} testid={`tour-save-${tour.id}`} />
                </div>
            </div>
        </Card>
    );
}

export default function ToursAdmin() {
    const tours = useTours(true);
    const invalidate = useInvalidate();
    const [err, setErr] = useState("");

    const create = async () => {
        setErr("");
        const { error } = await supabase
            .from("tours")
            .insert({ title: "Novo passeio", sort_order: 0, active: false });
        if (error) setErr("Erro ao criar: " + error.message);
        else invalidate("tours");
    };

    return (
        <div className="space-y-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <h1 className="font-display text-2xl font-black uppercase sm:text-3xl">
                    Passeios
                </h1>
                <button
                    onClick={create}
                    data-testid="tours-create"
                    className="rounded-full bg-neon-teal px-6 py-2.5 font-display text-[10px] font-extrabold uppercase tracking-widest text-ocean transition-transform hover:scale-105"
                >
                    + Cadastrar passeio
                </button>
            </div>
            <Err msg={err} />
            {tours.map((t) => (
                <TourRow key={t.id} tour={t} />
            ))}
            <AddonsAdmin />
        </div>
    );
}
